import {
  faCircleCheck,
  faCircleXmark,
  faClock,
  faMinus,
  faPlus,
  faCircleArrowLeft
} from '@fortawesome/pro-solid-svg-icons'

import {
  MEMBER_POINTS_TRANSACTION_TYPES,
  QB_TRANSACTIONS_TABLE_FIELDS
} from '../../constants/members'
import {
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  LOYALTY_EVENT_TYPES,
  ADDITIONAL_TXN_TYPES,
  EXCHANGE_TRANSACTION_TYPES
} from '../../constants/transactionTypes'
import {
  convertToDate,
  getDateDaysSubtractedBy,
  getTodaysDate
} from '../date.helpers'
import { extractBrandId, extractTokenId } from '../epics.helpers'
import {
  analyticsFilterData,
  buildDateRangeQueryParams,
  isAnalyticsPointsBank
} from '../analytics/analytics.helpers'
import { TOKEN_STATS_FILTER, TOKEN_TYPE } from '../../constants/token'
import { COLOR_PALETTE } from '../../constants/theme'

export const REWARD_TRANSACTION_TYPES = [
  LOYALTY_EVENT_TYPES.CODE_TO_POINTS,
  LOYALTY_EVENT_TYPES.CODE_TO_NFT,
  LOYALTY_EVENT_TYPES.CREDIT_POINTS,
  LOYALTY_EVENT_TYPES.CREDIT_NFT,
  LOYALTY_EVENT_TYPES.SIGNUP_BONUS
]

const handleGetFilterTypeForTransactions = (
  { type, transactionType, exchangeType },
  tokenId
) => {
  if (isAnalyticsPointsBank(type)) {
    return buildTypesParamsForPointsBank(transactionType)
  } else {
    return buildParamsForPointsExchange(exchangeType, tokenId)
  }
}

const buildTypesParamsForPointsBank = (transactionType) => {
  switch (transactionType) {
    case TRANSACTION_TYPES.REWARD: //reward
      return {
        types: REWARD_TRANSACTION_TYPES
      }
    case TRANSACTION_TYPES.REDEEM: //redeem
      return {
        types: [
          LOYALTY_EVENT_TYPES.DEBIT_POINTS,
          LOYALTY_EVENT_TYPES.POINTS_TO_REWARDS
        ]
      }
    case TRANSACTION_TYPES.EXCHANGE: //exchange
      return {
        types: [LOYALTY_EVENT_TYPES.EXCHANGE, LOYALTY_EVENT_TYPES.NFT_SALE]
      }
    default:
      return { types: [] }
  }
}

const buildParamsForPointsExchange = (exchangeType, tokenId) => {
  let params = {}
  switch (exchangeType) {
    case EXCHANGE_TRANSACTION_TYPES.BUY: //buy exchange
      params = { token_id: tokenId }
      break
    case EXCHANGE_TRANSACTION_TYPES.SELL: //sell exchange
      params = { token_to_id: tokenId }
      break
  }
  return {
    types: [LOYALTY_EVENT_TYPES.EXCHANGE, LOYALTY_EVENT_TYPES.REVERT_EXCHANGE],
    exclude_token_to_types: [TOKEN_TYPE.ERC_721],
    ...params
  }
}

const buildMainTransactionsTableHeaders = (t, tokenProfile) => {
  return [
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.NAME,
      label: t('members.name')
    },
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.EMAIL,
      label: t('members.email')
    },
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.AUTH_ID,
      label: t('members.member-auth-id')
    },
    ...buildTransactionsTableHeaders(t, tokenProfile)
  ]
}

const buildTransactionsTableHeaders = (t, { symbol }) => {
  return [
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.STATUS,
      label: t('common.status')
    },
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.DATE_OF_TRANSACTION,
      label: t('members.date-of-transaction')
    },
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.TRANSACTION_HASH,
      label: t('members.transaction-hash')
    },
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.TRANSACTION_TYPE,
      label: t('members.transaction-type')
    },
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.AMOUNT_OF_CURRENCY,
      label: t('members.amount-of-currency', { symbol })
    },
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.AMOUNT_OF_EXCHANGE_CURRENCY,
      label: t('members.amount-of-exchange-currency')
    },
    {
      value: QB_TRANSACTIONS_TABLE_FIELDS.EXCHANGE_CURRENCY,
      label: t('members.exchange-currency')
    }
  ]
}

const getLastCurrentWeekDateRanges = () => {
  return [
    {
      startDate: convertToDate(getDateDaysSubtractedBy(6)),
      endDate: getTodaysDate(),
      filterBy: TOKEN_STATS_FILTER.THIS_WEEK
    },
    {
      startDate: convertToDate(getDateDaysSubtractedBy(13)),
      endDate: convertToDate(getDateDaysSubtractedBy(7)),
      filterBy: TOKEN_STATS_FILTER.LAST_WEEK
    }
  ]
}

const getTransactionUser = (user, userTo, brandTo, currentBrandId) => {
  if (brandTo?.id === currentBrandId) {
    return userTo
  } else {
    return user
  }
}

const buildDefaultParamsWithFilters = (state) => {
  const { analyticsFilter, brandId, tokenId } =
    getBrandIdTokenIdAnalyticsFilters(state)
  return {
    brandId,
    tokenId,
    ...handleGetFilterTypeForTransactions(analyticsFilter, tokenId),
    ...buildDateRangeQueryParams(analyticsFilter)
  }
}

const getBrandIdTokenIdAnalyticsFilters = (state) => {
  const brandId = extractBrandId(state)
  const tokenId = extractTokenId(state)
  const analyticsFilter = analyticsFilterData(state)
  return {
    brandId,
    tokenId,
    analyticsFilter
  }
}

const buildTxnStatusColorIcon = (status) => {
  switch (status) {
    case TRANSACTION_STATUS.SUCCESS:
      return { icon: faCircleCheck, color: COLOR_PALETTE.SUCCESS }
    case TRANSACTION_STATUS.FAILED:
      return { icon: faCircleXmark, color: COLOR_PALETTE.ERROR }
    case TRANSACTION_STATUS.REVERSE:
      return { icon: faCircleArrowLeft, color: COLOR_PALETTE.WARNING }
    default:
      return { icon: faClock, color: COLOR_PALETTE.WARNING }
  }
}

const buildMemberTransactionTypeIconColor = (type) => {
  if (type === MEMBER_POINTS_TRANSACTION_TYPES.CREDIT_POINTS) {
    return {
      icon: faPlus,
      color: COLOR_PALETTE.SUCCESS
    }
  }
  return {
    icon: faMinus,
    color: COLOR_PALETTE.ERROR
  }
}

const buildEventGroup = (loyaltyEventType) => {
  switch (loyaltyEventType) {
    case LOYALTY_EVENT_TYPES.CODE_TO_POINTS:
    case LOYALTY_EVENT_TYPES.CODE_TO_NFT:
    case LOYALTY_EVENT_TYPES.CREDIT_POINTS:
    case LOYALTY_EVENT_TYPES.SIGNUP_BONUS:
      return TRANSACTION_TYPES.REWARD
    case LOYALTY_EVENT_TYPES.DEBIT_POINTS:
    case LOYALTY_EVENT_TYPES.POINTS_TO_REWARDS:
      return TRANSACTION_TYPES.REDEEM
    case LOYALTY_EVENT_TYPES.EXCHANGE:
      return TRANSACTION_TYPES.EXCHANGE
    case LOYALTY_EVENT_TYPES.NFT_TRANSFER:
      return ADDITIONAL_TXN_TYPES.TRANSFER
    default:
      return loyaltyEventType
  }
}

const isEventTypeRevertExchange = (eventType) => {
  return eventType === LOYALTY_EVENT_TYPES.REVERT_EXCHANGE
}

const isEventTypeNftTransfer = (eventType) => {
  return eventType === LOYALTY_EVENT_TYPES.NFT_TRANSFER
}

const isEventTypeNftSale = (eventType) => {
  return eventType === LOYALTY_EVENT_TYPES.NFT_SALE
}

export {
  handleGetFilterTypeForTransactions,
  buildMainTransactionsTableHeaders,
  buildTransactionsTableHeaders,
  getTransactionUser,
  buildTxnStatusColorIcon,
  getLastCurrentWeekDateRanges,
  buildDefaultParamsWithFilters,
  getBrandIdTokenIdAnalyticsFilters,
  buildMemberTransactionTypeIconColor,
  buildEventGroup,
  isEventTypeRevertExchange,
  isEventTypeNftTransfer,
  isEventTypeNftSale
}
